// src/pages/Admin.jsx
import React, {
  useState, useEffect, useCallback, useMemo, useRef, // ── ADDED useRef
} from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebase'; // ── ADDED storage
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection, collectionGroup, addDoc, updateDoc, deleteDoc,
  doc, query, orderBy, limit, onSnapshot, where, serverTimestamp, setDoc, // ── ADDED setDoc
} from 'firebase/firestore';
import {                                                                    // ── ADDED storage helpers
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const formatDate = (ts) => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  } catch {
    return '—';
  }
};

const formatDateOnly = (ts) => {
  if (!ts) return 'Unknown';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
};

const timeAgo = (ts) => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
};

// ── ADDED: byte-size formatter for APK uploads
const formatBytes = (b) => {
  if (!b) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / 1024 ** i).toFixed(1)} ${units[i]}`;
};

const useDebounced = (value, delay = 150) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const isUserApproved = (u) =>
  u?.isApproved === true ||
  u?.approved === true ||
  u?.registrationStatus === 'approved';

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose, message]);

  const config =
    {
      success: { bg: 'bg-green-500', icon: 'fa-circle-check' },
      error: { bg: 'bg-red-500', icon: 'fa-circle-exclamation' },
      warning: { bg: 'bg-yellow-500', icon: 'fa-triangle-exclamation' },
      info: { bg: 'bg-blue-500', icon: 'fa-circle-info' },
    }[type] || { bg: 'bg-green-500', icon: 'fa-circle-check' };

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 340, damping: 26 }}
      className={`fixed bottom-4 right-4 left-4 sm:left-auto z-[200] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 sm:max-w-sm ${config.bg}`}
    >
      <i className={`fas ${config.icon}`} aria-hidden="true" />
      <span className="text-sm font-medium flex-1">{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="text-white/80 hover:text-white"
      >
        <i className="fas fa-times text-xs" aria-hidden="true" />
      </button>
    </motion.div>
  );
};

/* ================================================================== */
/* Confirm Dialog                                                      */
/* ================================================================== */

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = true,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onCancel, onConfirm]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[150] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onCancel}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ scale: 0.95, y: 8 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 8 }}
            transition={{ type: 'spring', stiffness: 340, damping: 26 }}
            className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  danger ? 'bg-red-100 dark:bg-red-900/30' : 'bg-orange/10'
                }`}
              >
                <i
                  className={`fas fa-exclamation-triangle ${
                    danger ? 'text-red-500' : 'text-orange'
                  }`}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                  {title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {message}
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm transition"
              >
                {cancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className={`px-4 py-2 text-white rounded-lg text-sm font-medium transition ${
                  danger
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-orange hover:bg-orange-dark'
                }`}
              >
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Skeletons                                                           */
/* ================================================================== */

const TableSkeleton = ({ rows = 6 }) => (
  <div className="p-4 space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div
        key={i}
        className="h-12 bg-gray-200 dark:bg-gray-700/50 rounded animate-pulse"
      />
    ))}
  </div>
);

const CardSkeleton = ({ count = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 animate-pulse"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-2 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
        <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/* Nav items                                                           */
/* ================================================================== */

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line', color: 'text-orange' },
  { id: 'moderation', label: 'Moderation', icon: 'fa-shield-halved', color: 'text-red-500' },
  { id: 'users', label: 'Users', icon: 'fa-users', color: 'text-blue-500' },
  { id: 'comments', label: 'Comments', icon: 'fa-comment-dots', color: 'text-yellow-500' },
  { id: 'contacts', label: 'Contacts', icon: 'fa-envelope', color: 'text-teal-500' },
  { id: 'notifications', label: 'Notifications', icon: 'fa-bell', color: 'text-yellow-600' },
  { id: 'android-app', label: 'Android App', icon: 'fa-android', color: 'text-green-500' }, // ── ADDED
];

/* ================================================================== */
/* Admin                                                               */
/* ================================================================== */

const Admin = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();

  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const showToast = useCallback(
    (message, type = 'success') => setToast({ message, type }),
    [],
  );
  const hideToast = useCallback(() => setToast(null), []);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setConfirmState({
        title: opts.title || 'Confirm',
        message: opts.message || 'Are you sure?',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        danger: opts.danger !== false,
        resolve,
      });
    });
  }, []);

  const handleConfirmYes = () => {
    confirmState?.resolve(true);
    setConfirmState(null);
  };
  const handleConfirmNo = () => {
    confirmState?.resolve(false);
    setConfirmState(null);
  };

  /* ============ Stats ============ */
  const [stats, setStats] = useState({
    members: 0,
    board: 0,
    admins: 0,
    pendingPosts: 0,
    pendingStories: 0,
    pendingUsers: 0,
    totalPosts: 0,
    totalComments: 0,
    unreadContacts: 0,
  });

  /* ============ Data ============ */
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingStories, setPendingStories] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [notificationsList, setNotificationsList] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  /* ============ Notifications form ============ */
  const [notifForm, setNotifForm] = useState({
    title: '',
    body: '',
    recipientType: 'all',
    roles: [],
    userIds: [],
    editId: null,
  });
  const [sendNotifModal, setSendNotifModal] = useState(false);
  const [sendingNotif, setSendingNotif] = useState(false);
  const [userPickerSearch, setUserPickerSearch] = useState('');

  const functions = useMemo(() => getFunctions(), []);
  const sendApprovalEmail = useMemo(
    () => httpsCallable(functions, 'sendApprovalEmail'),
    [functions],
  );
  const deleteUserAccount = useMemo(
    () => httpsCallable(functions, 'deleteUserAccount'),
    [functions],
  );
  const createUserAccount = useMemo(
    () => httpsCallable(functions, 'createUserAccount'),
    [functions],
  );

  /* ================================================================== */
  /* Live subscriptions                                                  */
  /* ================================================================== */

  useEffect(() => {
    if (!isAdmin) return undefined;

    setUsersLoading(true);
    const unsub = onSnapshot(
      collection(db, 'users'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(list);
        let member = 0, boardCount = 0, adminCount = 0, pending = 0;
        list.forEach((u) => {
          if (u.isAdmin) adminCount++;
          else if (u.role === 'Board' || u.role === 'board') boardCount++;
          else member++;
          if (!isUserApproved(u) && u.registrationStatus !== 'rejected')
            pending++;
        });
        setStats((s) => ({
          ...s,
          members: member,
          board: boardCount,
          admins: adminCount,
          pendingUsers: pending,
        }));
        setUsersLoading(false);
      },
      (err) => {
        console.warn('users listener:', err);
        setUsersLoading(false);
      },
    );
    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const unsub = onSnapshot(
      collection(db, 'posts'),
      (snap) => setStats((s) => ({ ...s, totalPosts: snap.size })),
      (err) => console.warn('posts listener:', err),
    );
    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    setPostsLoading(true);
    const q = query(
      collection(db, 'posts'),
      where('approved', '==', false),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setPendingPosts(list);
        setStats((s) => ({ ...s, pendingPosts: list.length }));
        setPostsLoading(false);
      },
      (err) => {
        console.warn('pending posts listener:', err);
        setPostsLoading(false);
      },
    );
    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const q = query(
      collection(db, 'stories'),
      where('approved', '==', false),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setPendingStories(list);
        setStats((s) => ({ ...s, pendingStories: list.length }));
      },
      (err) => console.warn('pending stories listener:', err),
    );
    return () => unsub();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    setNotificationsLoading(true);
    const q = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotificationsList(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })),
        );
        setNotificationsLoading(false);
      },
      (err) => {
        console.warn('notifications listener:', err);
        setNotificationsLoading(false);
      },
    );
    return () => unsub();
  }, [isAdmin]);

  /* ================================================================== */
  /* Live contacts inbox — the key wiring for the Contacts tab          */
  /* ================================================================== */

  useEffect(() => {
    if (!isAdmin) return undefined;
    setContactsLoading(true);
    const q = query(
      collection(db, 'contacts'),
      orderBy('createdAt', 'desc'),
      limit(200),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            // Normalize so timeAgo / formatDate have a real Date
            createdAt: data.createdAt?.toDate?.() || data.createdAt || null,
          };
        });
        setContacts(list);
        const unread = list.filter((c) => c.status !== 'read').length;
        setStats((s) => ({ ...s, unreadContacts: unread }));
        setContactsLoading(false);
      },
      (err) => {
        console.warn('contacts listener:', err);
        setContactsLoading(false);
      },
    );
    return () => unsub();
  }, [isAdmin]);

  /* ================================================================== */
  /* Live comments via collectionGroup                                   */
  /* ================================================================== */

  useEffect(() => {
    if (!isAdmin) return undefined;
    setCommentsLoading(true);

    const q = query(
      collectionGroup(db, 'comments'),
      orderBy('createdAt', 'desc'),
      limit(200),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            postId: d.ref.parent.parent?.id || data.postId || null,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(0),
          };
        });
        setComments(list);
        setStats((s) => ({ ...s, totalComments: list.length }));
        setCommentsLoading(false);
      },
      (err) => {
        console.warn('comments listener:', err);
        setCommentsLoading(false);
      },
    );

    return () => unsub();
  }, [isAdmin]);

  /* ================================================================== */
  /* Sidebar + modal Esc handlers                                        */
  /* ================================================================== */

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setSidebarOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  useEffect(() => {
    if (!sendNotifModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !sendingNotif) {
        setSendNotifModal(false);
        resetNotifForm();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line
  }, [sendNotifModal, sendingNotif]);

  /* ================================================================== */
  /* Users handlers                                                      */
  /* ================================================================== */

  const updateUserField = async (userId, field, value) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        [field]: value,
        updatedAt: serverTimestamp(),
      });
      showToast('User updated');
    } catch (err) {
      console.error(err);
      showToast('Failed to update user', 'error');
    }
  };

  const updateUserFields = async (userId, fields) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        ...fields,
        updatedAt: serverTimestamp(),
      });
      showToast('User updated');
    } catch (err) {
      console.error(err);
      showToast('Failed to update user', 'error');
    }
  };

  const approveUser = async (u) => {
    try {
      await updateDoc(doc(db, 'users', u.id), {
        isApproved: true,
        approved: true,
        registrationStatus: 'approved',
        approvedAt: serverTimestamp(),
        approvedBy: user?.email || user?.uid,
        updatedAt: serverTimestamp(),
      });

      addDoc(collection(db, 'notifications'), {
        title: 'Account Approved! 🎉',
        body: 'Your account has been approved. Welcome to the community!',
        type: 'user_approved',
        recipientType: 'users',
        recipients: [u.id],
        createdAt: serverTimestamp(),
        readBy: [],
        link: '/',
        createdBy: user?.uid || null,
        createdByName: user?.displayName || user?.email || 'Admin',
      }).catch(() => {});

      try {
        await sendApprovalEmail({ uid: u.id, type: 'approve' });
        showToast(`${u.email} approved. Email sent.`);
      } catch (emailErr) {
        console.error('Email failed:', emailErr);
        showToast(`${u.email} approved, but email failed.`, 'warning');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to approve user', 'error');
    }
  };

  const rejectUser = async (u) => {
    const ok = await confirm({
      title: 'Reject this user?',
      message: `${u.email} will be rejected and notified by email.`,
      confirmLabel: 'Reject',
    });
    if (!ok) return;
    try {
      await updateDoc(doc(db, 'users', u.id), {
        registrationStatus: 'rejected',
        rejectedAt: serverTimestamp(),
        rejectedBy: user?.email || user?.uid,
        updatedAt: serverTimestamp(),
      });

      addDoc(collection(db, 'notifications'), {
        title: 'Registration Not Approved',
        body: 'Your registration was not approved. Contact support if you have questions.',
        type: 'user_rejected',
        recipientType: 'users',
        recipients: [u.id],
        createdAt: serverTimestamp(),
        readBy: [],
        link: '/',
        createdBy: user?.uid || null,
        createdByName: user?.displayName || user?.email || 'Admin',
      }).catch(() => {});

      try {
        await sendApprovalEmail({ uid: u.id, type: 'reject' });
        showToast(`${u.email} rejected. Email sent.`);
      } catch (emailErr) {
        console.error('Email failed:', emailErr);
        showToast(`${u.email} rejected, but email failed.`, 'warning');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to reject user', 'error');
    }
  };

  const deleteUser = async (u) => {
    const ok = await confirm({
      title: 'Delete user?',
      message: `${u.email} will be permanently removed. This cannot be undone.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteUserAccount({ uid: u.id });
      showToast(`${u.email} deleted`);
    } catch (err) {
      console.error('deleteUser failed:', err);
      const code = err?.code || '';
      let msg = err?.message || 'Failed to delete user';
      if (code === 'functions/not-found') {
        msg =
          'Server function not deployed. Run: firebase deploy --only functions:deleteUserAccount';
      } else if (code === 'functions/permission-denied') {
        msg = 'Only admins can delete users.';
      } else if (code === 'functions/unauthenticated') {
        msg = 'Please sign in again.';
      } else if (code === 'functions/invalid-argument') {
        msg = err?.message || 'Invalid request.';
      }
      showToast(msg, 'error');
    }
  };

  const inviteUser = async ({ email, displayName, role, sendInvite }) => {
    try {
      await createUserAccount({ email, displayName, role, sendInvite });
      showToast(`Invite sent to ${email}`);
    } catch (err) {
      console.error('inviteUser failed:', err);
      const code = err?.code || '';
      let msg = err?.message || 'Failed to invite user';
      if (code === 'functions/not-found') {
        msg =
          'Server function not deployed. Run: firebase deploy --only functions:createUserAccount';
      } else if (code === 'functions/permission-denied') {
        msg = 'Only admins can invite users.';
      } else if (code === 'functions/unauthenticated') {
        msg = 'Please sign in again.';
      }
      showToast(msg, 'error');
      throw err;
    }
  };

  /* ================================================================== */
  /* Moderation handlers                                                 */
  /* ================================================================== */

  const approvePost = async (p) => {
    try {
      await updateDoc(doc(db, 'posts', p.id), {
        approved: true,
        approvalStatus: 'approved',
        approvedBy: user.uid,
        approvedAt: serverTimestamp(),
      });
      addDoc(collection(db, 'notifications'), {
        title: '✅ Post Approved',
        body: `Your post${
          p.content
            ? ` "${p.content.slice(0, 60)}${p.content.length > 60 ? '…' : ''}"`
            : ''
        } is now visible to everyone.`,
        type: 'post_approved',
        recipientType: 'users',
        recipients: [p.authorId],
        createdAt: serverTimestamp(),
        readBy: [],
        link: '/',
        createdBy: user.uid,
        createdByName: user?.displayName || user?.email || 'Admin',
      }).catch(() => {});
      showToast('Post approved');
    } catch (err) {
      console.error(err);
      showToast('Failed to approve post', 'error');
    }
  };

  const rejectPost = async (p) => {
    const ok = await confirm({
      title: 'Reject this post?',
      message: `The post from ${p.authorName} will be deleted and they will be notified.`,
      confirmLabel: 'Reject & Delete',
    });
    if (!ok) return;
    try {
      addDoc(collection(db, 'notifications'), {
        title: '❌ Post Not Approved',
        body: 'Your post was not approved by an administrator.',
        type: 'post_rejected',
        recipientType: 'users',
        recipients: [p.authorId],
        createdAt: serverTimestamp(),
        readBy: [],
        link: '/',
        createdBy: user.uid,
        createdByName: user?.displayName || user?.email || 'Admin',
      }).catch(() => {});
      await deleteDoc(doc(db, 'posts', p.id));
      showToast('Post rejected');
    } catch (err) {
      console.error(err);
      showToast('Failed to reject post', 'error');
    }
  };

  const approveStory = async (s) => {
    try {
      await updateDoc(doc(db, 'stories', s.id), {
        approved: true,
        approvalStatus: 'approved',
        approvedBy: user.uid,
        approvedAt: serverTimestamp(),
      });
      addDoc(collection(db, 'notifications'), {
        title: '✅ Story Approved',
        body: 'Your story is now live and visible to everyone.',
        type: 'story_approved',
        recipientType: 'users',
        recipients: [s.authorId],
        createdAt: serverTimestamp(),
        readBy: [],
        link: '/',
        createdBy: user.uid,
        createdByName: user?.displayName || user?.email || 'Admin',
      }).catch(() => {});
      showToast('Story approved');
    } catch (err) {
      console.error(err);
      showToast('Failed to approve story', 'error');
    }
  };

  const rejectStory = async (s) => {
    const ok = await confirm({
      title: 'Reject this story?',
      message: `The story from ${s.authorName} will be deleted.`,
      confirmLabel: 'Reject & Delete',
    });
    if (!ok) return;
    try {
      addDoc(collection(db, 'notifications'), {
        title: '❌ Story Not Approved',
        body: 'Your story was not approved.',
        type: 'story_rejected',
        recipientType: 'users',
        recipients: [s.authorId],
        createdAt: serverTimestamp(),
        readBy: [],
        link: '/',
        createdBy: user.uid,
        createdByName: user?.displayName || user?.email || 'Admin',
      }).catch(() => {});
      await deleteDoc(doc(db, 'stories', s.id));
      showToast('Story rejected');
    } catch (err) {
      console.error(err);
      showToast('Failed to reject story', 'error');
    }
  };

  /* ================================================================== */
  /* Comments handlers                                                   */
  /* ================================================================== */

  const deleteComment = async (c) => {
    if (!c.postId) {
      showToast('Cannot determine parent post', 'error');
      return;
    }
    const ok = await confirm({
      title: 'Delete comment?',
      message: `Delete this comment from ${c.postAuthor || 'a post'}?`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'posts', c.postId, 'comments', c.id));
      showToast('Comment deleted');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete comment', 'error');
    }
  };

  const updateComment = async (c, newText) => {
    if (!c.postId) {
      showToast('Cannot determine parent post', 'error');
      return;
    }
    if (!newText.trim()) {
      showToast('Comment cannot be empty', 'warning');
      return;
    }
    try {
      await updateDoc(doc(db, 'posts', c.postId, 'comments', c.id), {
        text: newText.trim(),
        editedAt: serverTimestamp(),
        editedBy: user?.uid || null,
      });
      showToast('Comment updated');
    } catch (err) {
      console.error(err);
      showToast('Failed to update comment', 'error');
    }
  };

  /* ================================================================== */
  /* Contacts handlers                                                   */
  /* ================================================================== */

  const toggleContactRead = async (contact) => {
    const nextStatus = contact.status === 'read' ? 'unread' : 'read';
    try {
      await updateDoc(doc(db, 'contacts', contact.id), {
        status: nextStatus,
        readAt: nextStatus === 'read' ? serverTimestamp() : null,
        readBy: nextStatus === 'read' ? user?.uid || null : null,
      });
      showToast(
        nextStatus === 'read' ? 'Marked as read' : 'Marked as unread',
      );
    } catch (err) {
      console.error(err);
      showToast('Failed to update contact', 'error');
    }
  };

  const deleteContact = async (contact) => {
    const ok = await confirm({
      title: 'Delete message?',
      message: `Delete the message from ${contact.name} (${contact.email})?`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'contacts', contact.id));
      showToast('Message deleted');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete message', 'error');
    }
  };

  const bulkDeleteContacts = async (ids, onDone) => {
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} message${ids.length === 1 ? '' : 's'}?`,
      message: 'These contact messages will be permanently removed.',
      confirmLabel: 'Delete All',
    });
    if (!ok) return;
    try {
      await Promise.all(ids.map((id) => deleteDoc(doc(db, 'contacts', id))));
      showToast(`${ids.length} message${ids.length === 1 ? '' : 's'} deleted`);
      onDone?.();
    } catch (err) {
      console.error(err);
      showToast('Failed to delete some messages', 'error');
    }
  };

  /* ================================================================== */
  /* Notifications handlers                                              */
  /* ================================================================== */

  const resetNotifForm = useCallback(() => {
    setNotifForm({
      title: '',
      body: '',
      recipientType: 'all',
      roles: [],
      userIds: [],
      editId: null,
    });
    setUserPickerSearch('');
  }, []);

  const sendNotification = async () => {
    const { title, body, recipientType, roles, userIds, editId } = notifForm;
    if (!title.trim() || !body.trim()) {
      showToast('Title and body required', 'warning');
      return;
    }
    const recipients =
      recipientType === 'roles'
        ? roles
        : recipientType === 'users'
        ? userIds
        : [];
    if (recipientType !== 'all' && recipients.length === 0) {
      showToast(
        `Select at least one ${recipientType === 'roles' ? 'role' : 'user'}`,
        'warning',
      );
      return;
    }
    setSendingNotif(true);
    try {
      if (editId) {
        await updateDoc(doc(db, 'notifications', editId), {
          title: title.trim(),
          body: body.trim(),
          recipientType,
          recipients,
          updatedAt: serverTimestamp(),
        });
        showToast('Notification updated');
      } else {
        await addDoc(collection(db, 'notifications'), {
          title: title.trim(),
          body: body.trim(),
          type: 'announcement',
          recipientType,
          recipients,
          createdAt: serverTimestamp(),
          readBy: [],
          createdBy: user?.uid || null,
          createdByName: user?.displayName || user?.email || 'Admin',
        });
        showToast('Notification sent');
      }
      resetNotifForm();
      setSendNotifModal(false);
    } catch (err) {
      console.error(err);
      showToast('Failed to save notification', 'error');
    } finally {
      setSendingNotif(false);
    }
  };

  const handleEditNotification = (n) => {
    setNotifForm({
      title: n.title || '',
      body: n.body || '',
      recipientType: n.recipientType || 'all',
      roles: n.recipientType === 'roles' ? n.recipients || [] : [],
      userIds: n.recipientType === 'users' ? n.recipients || [] : [],
      editId: n.id,
    });
    setUserPickerSearch('');
    setSendNotifModal(true);
  };

  const deleteNotification = async (id) => {
    const ok = await confirm({
      title: 'Delete notification?',
      message: 'Users who already saw it keep the read state.',
    });
    if (!ok) return;
    try {
      await deleteDoc(doc(db, 'notifications', id));
      showToast('Notification deleted');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete notification', 'error');
    }
  };

  const bulkDeleteNotifications = async (ids, onDone) => {
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} notification${ids.length === 1 ? '' : 's'}?`,
      message: 'These notifications will be permanently removed.',
      confirmLabel: 'Delete All',
    });
    if (!ok) return;
    try {
      await Promise.all(
        ids.map((id) => deleteDoc(doc(db, 'notifications', id))),
      );
      showToast(
        `${ids.length} notification${ids.length === 1 ? '' : 's'} deleted`,
      );
      onDone?.();
    } catch (err) {
      console.error(err);
      showToast('Failed to delete some notifications', 'error');
    }
  };

  /* ================================================================== */
  /* User picker helpers                                                 */
  /* ================================================================== */

  const pickerUsers = useMemo(() => {
    const q = userPickerSearch.trim().toLowerCase();
    const base = users
      .slice()
      .sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    if (!q) return base;
    return base.filter(
      (u) =>
        (u.email || '').toLowerCase().includes(q) ||
        (u.displayName || u.name || '').toLowerCase().includes(q),
    );
  }, [users, userPickerSearch]);

  const allPickerSelected =
    pickerUsers.length > 0 &&
    pickerUsers.every((u) => notifForm.userIds.includes(u.id));

  const toggleAllPickerUsers = () => {
    if (allPickerSelected) {
      const visibleIds = new Set(pickerUsers.map((u) => u.id));
      setNotifForm({
        ...notifForm,
        userIds: notifForm.userIds.filter((id) => !visibleIds.has(id)),
      });
    } else {
      const merged = new Set([
        ...notifForm.userIds,
        ...pickerUsers.map((u) => u.id),
      ]);
      setNotifForm({ ...notifForm, userIds: [...merged] });
    }
  };

  /* ================================================================== */
  /* Auth gate                                                           */
  /* ================================================================== */

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange border-t-transparent" />
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500 font-medium px-4 text-center">
        <div>
          <i
            className="fas fa-lock text-4xl mb-3 block"
            aria-hidden="true"
          />
          Access denied. Admin only.
        </div>
      </div>
    );
  }

  /* ================================================================== */
  /* Render content                                                      */
  /* ================================================================== */

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardTab stats={stats} onNavigate={(t) => setActiveTab(t)} />
        );
      case 'moderation':
        return (
          <ModerationTab
            pendingPosts={pendingPosts}
            pendingStories={pendingStories}
            loading={postsLoading}
            onApprovePost={approvePost}
            onRejectPost={rejectPost}
            onApproveStory={approveStory}
            onRejectStory={rejectStory}
          />
        );
      case 'users':
        return (
          <UsersTab
            users={users}
            loading={usersLoading}
            showToast={showToast}
            onApprove={approveUser}
            onReject={rejectUser}
            onDelete={deleteUser}
            onToggleAdmin={(id, v) => updateUserField(id, 'isAdmin', v)}
            onToggleBlock={(id, v) => updateUserField(id, 'isBlocked', v)}
            onUpdateFields={updateUserFields}
            onInvite={inviteUser}
          />
        );
      case 'comments':
        return (
          <CommentsTab
            comments={comments}
            loading={commentsLoading}
            onDelete={deleteComment}
            onUpdate={updateComment}
          />
        );
      case 'contacts':
        return (
          <ContactsTab
            contacts={contacts}
            loading={contactsLoading}
            onToggleRead={toggleContactRead}
            onDelete={deleteContact}
            onBulkDelete={bulkDeleteContacts}
          />
        );
      case 'notifications':
        return (
          <NotificationsTab
            notificationsList={notificationsList}
            loading={notificationsLoading}
            onDelete={deleteNotification}
            onBulkDelete={bulkDeleteNotifications}
            onEdit={handleEditNotification}
            onSend={() => {
              resetNotifForm();
              setSendNotifModal(true);
            }}
          />
        );
      // ── ADDED
      case 'android-app':
        return (
          <AndroidAppTab
            user={user}
            showToast={showToast}
            confirm={confirm}
          />
        );
      default:
        return (
          <DashboardTab stats={stats} onNavigate={(t) => setActiveTab(t)} />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
      <AnimatePresence>
        {toast && (
          <Toast
            key="toast"
            message={toast.message}
            type={toast.type}
            onClose={hideToast}
          />
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        cancelLabel={confirmState?.cancelLabel}
        danger={confirmState?.danger}
        onConfirm={handleConfirmYes}
        onCancel={handleConfirmNo}
      />

      {/* Mobile sidebar toggle */}
      <button
        type="button"
        onClick={() => setSidebarOpen((s) => !s)}
        className="lg:hidden fixed top-20 left-4 z-[75] bg-orange text-white p-2 rounded-lg shadow-lg active:scale-95 transition"
        aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        <i
          className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`}
          aria-hidden="true"
        />
      </button>

      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-[60] backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-white dark:bg-gray-800 shadow-xl z-[70] transition-all duration-300 overflow-y-auto flex flex-col ${
          sidebarOpen
            ? 'w-64 translate-x-0'
            : 'w-64 -translate-x-full lg:w-20 lg:translate-x-0'
        }`}
      >
        <div className="p-4 border-b dark:border-gray-700 flex items-center gap-3">
          <img
            src="/images/iiee-logo.png"
            alt="IIEE-ERCSA logo"
            className="h-10 w-10 object-contain flex-shrink-0"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <div className={sidebarOpen ? '' : 'lg:hidden'}>
            <h1 className="font-bold text-sm text-gray-800 dark:text-white">
              IIEE-ERCSA
            </h1>
            <p className="text-xs text-gray-500">Admin Panel</p>
          </div>
        </div>

        <nav className="p-3 flex-1">
          {navItems.map((item) => {
            const badge =
              item.id === 'moderation'
                ? stats.pendingPosts + stats.pendingStories
                : item.id === 'contacts'
                ? stats.unreadContacts
                : 0;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setActiveTab(item.id);
                  if (window.innerWidth < 1024) setSidebarOpen(false);
                }}
                aria-current={activeTab === item.id ? 'page' : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all ${
                  activeTab === item.id
                    ? 'bg-orange/10 text-orange border-l-4 border-orange'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <i
                  className={`fas ${item.icon} ${
                    activeTab === item.id ? 'text-orange' : item.color
                  } text-lg w-5`}
                  aria-hidden="true"
                />
                <span
                  className={`${
                    sidebarOpen ? '' : 'lg:hidden'
                  } text-sm font-medium flex-1 text-left`}
                >
                  {item.label}
                </span>
                {badge > 0 && (
                  <span
                    className={`${
                      sidebarOpen ? '' : 'lg:hidden'
                    } bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center`}
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t dark:border-gray-700">
          <Link
            to="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-orange/10 hover:text-orange transition"
            title="View public site"
          >
            <i
              className="fas fa-external-link-alt text-lg w-5"
              aria-hidden="true"
            />
            <span
              className={`${sidebarOpen ? '' : 'lg:hidden'} text-sm font-medium`}
            >
              View Site
            </span>
          </Link>
        </div>
      </aside>

      {/* Mobile View Site button */}
      <Link
        to="/"
        className="lg:hidden fixed top-20 right-4 z-[75] bg-gray-800/90 text-white p-2 rounded-lg shadow-lg hover:bg-gray-900 transition active:scale-95"
        aria-label="View site"
        title="View site"
      >
        <i className="fas fa-external-link-alt" aria-hidden="true" />
      </Link>

      {/* Main */}
      <main
        className={`flex-1 transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        } pt-6 pb-12 px-4 min-w-0`}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="mb-6 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white relative inline-block">
                {navItems.find((i) => i.id === activeTab)?.label || 'Dashboard'}
                <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-orange rounded-full" />
              </h1>
              <p className="text-gray-600 dark:text-gray-300 text-sm mt-3">
                Manage{' '}
                {navItems
                  .find((i) => i.id === activeTab)
                  ?.label?.toLowerCase() || 'content'}
              </p>
            </div>

            <Link
              to="/"
              className="hidden lg:inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-orange hover:text-white hover:border-orange px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition"
            >
              <i className="fas fa-external-link-alt" aria-hidden="true" />
              View Site
            </Link>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Send / Edit Notification Modal */}
      <AnimatePresence>
        {sendNotifModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[150] p-4"
            onClick={() => {
              if (sendingNotif) return;
              setSendNotifModal(false);
              resetNotifForm();
            }}
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              initial={{ scale: 0.95, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              transition={{ type: 'spring', stiffness: 340, damping: 26 }}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                  {notifForm.editId ? 'Edit Notification' : 'Send Notification'}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    if (sendingNotif) return;
                    setSendNotifModal(false);
                    resetNotifForm();
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  aria-label="Close"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Title
                  </label>
                  <input
                    type="text"
                    value={notifForm.title}
                    onChange={(e) =>
                      setNotifForm({ ...notifForm, title: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-orange focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                    Body
                  </label>
                  <textarea
                    rows="4"
                    value={notifForm.body}
                    onChange={(e) =>
                      setNotifForm({ ...notifForm, body: e.target.value })
                    }
                    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-orange outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Recipients
                  </label>
                  <div className="flex flex-wrap gap-4 text-sm dark:text-gray-200">
                    {['all', 'roles', 'users'].map((t) => (
                      <label key={t} className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="recipientType"
                          value={t}
                          checked={notifForm.recipientType === t}
                          onChange={() =>
                            setNotifForm({ ...notifForm, recipientType: t })
                          }
                        />
                        {t === 'all'
                          ? 'All Users'
                          : t === 'roles'
                          ? 'By Role'
                          : 'Specific Users'}
                      </label>
                    ))}
                  </div>
                </div>

                {notifForm.recipientType === 'roles' && (
                  <div className="flex flex-wrap gap-3 text-sm dark:text-gray-200">
                    {['Admin', 'Board', 'Member'].map((role) => (
                      <label key={role} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={notifForm.roles.includes(role)}
                          onChange={(e) => {
                            setNotifForm({
                              ...notifForm,
                              roles: e.target.checked
                                ? [...notifForm.roles, role]
                                : notifForm.roles.filter((r) => r !== role),
                            });
                          }}
                        />
                        {role}
                      </label>
                    ))}
                  </div>
                )}

                {notifForm.recipientType === 'users' && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <i
                          className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
                          aria-hidden="true"
                        />
                        <input
                          type="text"
                          value={userPickerSearch}
                          onChange={(e) => setUserPickerSearch(e.target.value)}
                          placeholder="Search users..."
                          className="w-full pl-9 pr-3 py-1.5 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-orange focus:outline-none"
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                        {notifForm.userIds.length} selected
                      </span>
                    </div>

                    <label className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-50 dark:bg-gray-700/50 text-xs font-medium dark:text-gray-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allPickerSelected}
                        onChange={toggleAllPickerUsers}
                      />
                      <span>
                        Select All
                        {userPickerSearch.trim()
                          ? ` (filtered: ${pickerUsers.length})`
                          : ` (${pickerUsers.length})`}
                      </span>
                    </label>

                    <div className="max-h-48 overflow-y-auto border rounded-lg dark:border-gray-700">
                      {pickerUsers.length === 0 ? (
                        <p className="p-4 text-center text-xs text-gray-500">
                          No users match your search.
                        </p>
                      ) : (
                        pickerUsers.map((u) => (
                          <label
                            key={u.id}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b last:border-b-0 dark:border-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={notifForm.userIds.includes(u.id)}
                              onChange={(e) => {
                                setNotifForm({
                                  ...notifForm,
                                  userIds: e.target.checked
                                    ? [...notifForm.userIds, u.id]
                                    : notifForm.userIds.filter(
                                        (id) => id !== u.id,
                                      ),
                                });
                              }}
                            />
                            <span className="truncate">
                              {u.displayName || u.name
                                ? `${u.displayName || u.name} · `
                                : ''}
                              {u.email}
                            </span>
                          </label>
                        ))
                      )}
                    </div>

                    {notifForm.userIds.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setNotifForm({ ...notifForm, userIds: [] })
                        }
                        className="text-xs text-gray-500 hover:text-red-500 underline"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={sendNotification}
                    disabled={sendingNotif}
                    className="flex-1 bg-orange hover:bg-orange-dark text-white py-2 rounded-lg font-medium disabled:opacity-50 transition"
                  >
                    {sendingNotif
                      ? 'Saving...'
                      : notifForm.editId
                      ? 'Update'
                      : 'Send'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (sendingNotif) return;
                      setSendNotifModal(false);
                      resetNotifForm();
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ================================================================== */
/* Dashboard Tab                                                       */
/* ================================================================== */

const DashboardTab = ({ stats, onNavigate }) => {
  const cards = [
    { key: 'members', label: 'Members', value: stats.members, icon: 'fa-user-friends', color: 'text-blue-500' },
    { key: 'board', label: 'Board', value: stats.board, icon: 'fa-user-tie', color: 'text-purple-500' },
    { key: 'admins', label: 'Admins', value: stats.admins, icon: 'fa-user-shield', color: 'text-orange' },
    { key: 'pendingUsers', label: 'Pending Users', value: stats.pendingUsers, icon: 'fa-user-clock', color: 'text-yellow-500', to: 'users' },
    { key: 'totalPosts', label: 'Total Posts', value: stats.totalPosts, icon: 'fa-newspaper', color: 'text-green-500' },
    { key: 'pendingPosts', label: 'Pending Posts', value: stats.pendingPosts, icon: 'fa-clock', color: 'text-red-500', to: 'moderation' },
    { key: 'pendingStories', label: 'Pending Stories', value: stats.pendingStories, icon: 'fa-images', color: 'text-pink-500', to: 'moderation' },
    { key: 'totalComments', label: 'Comments', value: stats.totalComments, icon: 'fa-comment-dots', color: 'text-indigo-500', to: 'comments' },
    { key: 'unreadContacts', label: 'New Messages', value: stats.unreadContacts, icon: 'fa-envelope', color: 'text-teal-500', to: 'contacts' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => c.to && onNavigate(c.to)}
            className={`bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center transition-all ${
              c.to
                ? 'hover:shadow-lg hover:scale-[1.02] cursor-pointer active:scale-100'
                : 'cursor-default'
            }`}
          >
            <i
              className={`fas ${c.icon} text-2xl ${c.color} mb-2`}
              aria-hidden="true"
            />
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
              {c.value}
            </h3>
            <p className="text-xs text-gray-500">{c.label}</p>
          </button>
        ))}
      </div>

      {stats.totalPosts === 0 && stats.members === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 text-center text-sm text-gray-500">
          <i
            className="fas fa-seedling text-2xl text-orange mb-2 block"
            aria-hidden="true"
          />
          Fresh start — invite users or review pending posts.
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/* Moderation Tab                                                      */
/* ================================================================== */

const ModerationTab = ({
  pendingPosts,
  pendingStories,
  loading,
  onApprovePost,
  onRejectPost,
  onApproveStory,
  onRejectStory,
}) => {
  const [tab, setTab] = useState('posts');

  const totalPending = pendingPosts.length + pendingStories.length;

  return (
    <div>
      {totalPending > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
          <i className="fas fa-clock" aria-hidden="true" />
          <span>
            <strong>{totalPending}</strong> item
            {totalPending === 1 ? '' : 's'} waiting for review
          </span>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => setTab('posts')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
            tab === 'posts'
              ? 'bg-orange text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <i className="fas fa-newspaper" aria-hidden="true" />
          Posts
          {pendingPosts.length > 0 && (
            <span
              className={`text-[10px] px-1.5 rounded-full ${
                tab === 'posts'
                  ? 'bg-white/20'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              {pendingPosts.length}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setTab('stories')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
            tab === 'stories'
              ? 'bg-orange text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <i className="fas fa-images" aria-hidden="true" />
          Stories
          {pendingStories.length > 0 && (
            <span
              className={`text-[10px] px-1.5 rounded-full ${
                tab === 'stories'
                  ? 'bg-white/20'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              {pendingStories.length}
            </span>
          )}
        </button>
      </div>

      {loading && <CardSkeleton count={3} />}

      {!loading && tab === 'posts' && (
        <div className="space-y-3">
          {pendingPosts.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
              <i
                className="fas fa-check-circle text-4xl text-green-500 mb-3"
                aria-hidden="true"
              />
              <p className="text-gray-500">
                No pending posts. Everything is approved!
              </p>
            </div>
          ) : (
            pendingPosts.map((p) => (
              <div
                key={p.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow p-4"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-orange text-white flex items-center justify-center font-bold overflow-hidden flex-shrink-0">
                    {p.authorPhoto ? (
                      <img
                        src={p.authorPhoto}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      p.authorName?.[0]?.toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm dark:text-white truncate">
                      {p.authorName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(p.createdAt)}
                    </p>
                  </div>
                  <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs px-2 py-1 rounded-full font-medium flex-shrink-0">
                    Pending
                  </span>
                </div>

                {p.content && (
                  <p className="text-sm dark:text-gray-200 whitespace-pre-wrap break-words mb-3">
                    {p.content}
                  </p>
                )}

                {Array.isArray(p.media) && p.media.length > 0 ? (
                  <div className="grid grid-cols-3 gap-1 mb-3">
                    {p.media.map((m, i) =>
                      m.type === 'video' ? (
                        <video
                          key={i}
                          src={m.url}
                          className="w-full h-24 object-cover rounded"
                          muted
                        />
                      ) : (
                        <img
                          key={i}
                          src={m.url}
                          alt=""
                          className="w-full h-24 object-cover rounded"
                        />
                      ),
                    )}
                  </div>
                ) : (
                  Array.isArray(p.photos) &&
                  p.photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 mb-3">
                      {p.photos.map((u, i) => (
                        <img
                          key={i}
                          src={u}
                          alt=""
                          className="w-full h-24 object-cover rounded"
                        />
                      ))}
                    </div>
                  )
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onApprovePost(p)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg text-sm font-medium transition active:scale-[0.99]"
                  >
                    <i className="fas fa-check mr-1" aria-hidden="true" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => onRejectPost(p)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium transition active:scale-[0.99]"
                  >
                    <i className="fas fa-times mr-1" aria-hidden="true" />
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {!loading && tab === 'stories' && (
        <div className="space-y-3">
          {pendingStories.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
              <i
                className="fas fa-check-circle text-4xl text-green-500 mb-3"
                aria-hidden="true"
              />
              <p className="text-gray-500">
                No pending stories. Everything is approved!
              </p>
            </div>
          ) : (
            pendingStories.map((s) => (
              <div
                key={s.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow p-4"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-orange text-white flex items-center justify-center font-bold overflow-hidden flex-shrink-0">
                    {s.authorPhoto ? (
                      <img
                        src={s.authorPhoto}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      s.authorName?.[0]?.toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm dark:text-white truncate">
                      {s.authorName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDate(s.createdAt)}
                    </p>
                  </div>
                  <span className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs px-2 py-1 rounded-full font-medium flex-shrink-0">
                    Pending
                  </span>
                </div>

                {s.caption && (
                  <p className="text-sm dark:text-gray-200 mb-3">{s.caption}</p>
                )}

                {(s.items || []).length > 0 ? (
                  <div className="grid grid-cols-3 gap-1 mb-3">
                    {s.items.map((m, i) =>
                      m.type === 'video' ? (
                        <video
                          key={i}
                          src={m.url}
                          className="w-full h-24 object-cover rounded"
                          muted
                        />
                      ) : (
                        <img
                          key={i}
                          src={m.url}
                          alt=""
                          className="w-full h-24 object-cover rounded"
                        />
                      ),
                    )}
                  </div>
                ) : (
                  Array.isArray(s.photos) &&
                  s.photos.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 mb-3">
                      {s.photos.map((u, i) => (
                        <img
                          key={i}
                          src={u}
                          alt=""
                          className="w-full h-24 object-cover rounded"
                        />
                      ))}
                    </div>
                  )
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onApproveStory(s)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg text-sm font-medium transition active:scale-[0.99]"
                  >
                    <i className="fas fa-check mr-1" aria-hidden="true" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => onRejectStory(s)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm font-medium transition active:scale-[0.99]"
                  >
                    <i className="fas fa-times mr-1" aria-hidden="true" />
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/* Users Tab                                                           */
/* ================================================================== */

const PAGE_SIZE = 25;

const UsersTab = ({
  users,
  loading,
  onApprove,
  onReject,
  onDelete,
  onToggleAdmin,
  onToggleBlock,
  onUpdateFields,
  onInvite,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [profileModal, setProfileModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [inviteModal, setInviteModal] = useState(null);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set());

  const debouncedSearch = useDebounced(searchTerm, 150);

  const filtered = useMemo(() => {
    let list = users;
    if (filter === 'pending') {
      list = list.filter(
        (u) => !isUserApproved(u) && u.registrationStatus !== 'rejected',
      );
    } else if (filter === 'approved') {
      list = list.filter(isUserApproved);
    } else if (filter === 'rejected') {
      list = list.filter((u) => u.registrationStatus === 'rejected');
    } else if (filter === 'admins') {
      list = list.filter((u) => u.isAdmin);
    }
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          (u.email || '').toLowerCase().includes(q) ||
          (u.displayName || u.name || '').toLowerCase().includes(q),
      );
    }

    const bucket = (u) => {
      if (u.isAdmin) return 0;
      if (u.role === 'Board' || u.role === 'board') return 1;
      if (isUserApproved(u)) return 2;
      if (u.registrationStatus === 'rejected') return 4;
      return 3;
    };
    return list.slice().sort((a, b) => {
      const ba = bucket(a);
      const bb = bucket(b);
      if (ba !== bb) return ba - bb;
      return (a.email || '').localeCompare(b.email || '');
    });
  }, [users, debouncedSearch, filter]);

  useEffect(() => {
    setPage(0);
  }, [filter, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page],
  );

  const pendingCount = users.filter(
    (u) => !isUserApproved(u) && u.registrationStatus !== 'rejected',
  ).length;

  const saveEdit = async () => {
    if (!editModal) return;
    const { id, displayName, role, isAdmin, isBlocked } = editModal;
    await onUpdateFields?.(id, { displayName, role, isAdmin, isBlocked });
    setEditModal(null);
  };

  const submitInvite = async () => {
    if (!inviteModal?.email?.trim()) return;
    try {
      await onInvite?.(inviteModal);
      setInviteModal(null);
    } catch {
      /* toast handled upstream */
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = paginated.map((u) => u.id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
      <div className="p-4 border-b dark:border-gray-700 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <i
              className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search users"
              className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-orange focus:border-transparent outline-none"
            />
          </div>
          <button
            type="button"
            onClick={() =>
              setInviteModal({
                email: '',
                displayName: '',
                role: 'Member',
                sendInvite: true,
              })
            }
            className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 flex-shrink-0 transition"
          >
            <i className="fas fa-user-plus" aria-hidden="true" />
            <span className="hidden sm:inline">Invite User</span>
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'all', label: 'All', count: users.length },
            { id: 'pending', label: 'Pending', count: pendingCount },
            { id: 'approved', label: 'Approved', count: null },
            { id: 'rejected', label: 'Rejected', count: null },
            { id: 'admins', label: 'Admins', count: null },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                filter === f.id
                  ? 'bg-orange text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {f.label}
              {f.count !== null && ` (${f.count})`}
            </button>
          ))}
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-2 bg-orange/5 border border-orange/30 rounded-lg text-xs">
            <span className="font-medium text-orange">
              {selected.size} selected
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={async () => {
                for (const id of selected) {
                  const u = users.find((x) => x.id === id);
                  if (u && !isUserApproved(u)) await onApprove(u);
                }
                clearSelection();
              }}
              className="px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
            >
              <i className="fas fa-check mr-1" aria-hidden="true" /> Approve
            </button>
            <button
              type="button"
              onClick={async () => {
                for (const id of selected) {
                  const u = users.find((x) => x.id === id);
                  if (u) await onDelete(u);
                }
                clearSelection();
              }}
              className="px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white"
            >
              <i className="fas fa-trash-alt mr-1" aria-hidden="true" /> Delete
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <TableSkeleton rows={6} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 text-left">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={
                      paginated.length > 0 &&
                      paginated.every((u) => selected.has(u.id))
                    }
                    onChange={toggleSelectAllVisible}
                    aria-label="Select all visible"
                  />
                </th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Approval</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {paginated.map((u) => (
                <tr
                  key={u.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(u.id)}
                      onChange={() => toggleSelect(u.id)}
                      aria-label={`Select ${u.email}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                    {u.email}
                  </td>
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                    {u.displayName || u.name || '-'}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        u.isAdmin
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                          : u.role === 'Board' || u.role === 'board'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {u.isAdmin ? 'Admin' : u.role || 'Member'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        u.isBlocked
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}
                    >
                      {u.isBlocked ? 'Blocked' : 'Active'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {isUserApproved(u) ? (
                      <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded text-xs">
                        <i
                          className="fas fa-check-circle text-xs mr-1"
                          aria-hidden="true"
                        />
                        Approved
                      </span>
                    ) : u.registrationStatus === 'rejected' ? (
                      <span className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 rounded text-xs">
                        <i
                          className="fas fa-times-circle text-xs mr-1"
                          aria-hidden="true"
                        />
                        Rejected
                      </span>
                    ) : (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => onApprove(u)}
                          className="bg-green-500 hover:bg-green-600 text-white px-2 py-0.5 rounded text-xs transition"
                        >
                          <i
                            className="fas fa-check text-xs mr-1"
                            aria-hidden="true"
                          />
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => onReject(u)}
                          className="bg-red-500 hover:bg-red-600 text-white px-2 py-0.5 rounded text-xs transition"
                        >
                          <i
                            className="fas fa-times text-xs mr-1"
                            aria-hidden="true"
                          />
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 space-x-2 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setProfileModal(u)}
                      className="text-blue-500 text-xs"
                    >
                      <i className="fas fa-eye mr-1" aria-hidden="true" /> View
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setEditModal({
                          id: u.id,
                          displayName: u.displayName || u.name || '',
                          role: u.role || 'Member',
                          isAdmin: !!u.isAdmin,
                          isBlocked: !!u.isBlocked,
                        })
                      }
                      className="text-green-600 text-xs"
                    >
                      <i className="fas fa-pen mr-1" aria-hidden="true" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleAdmin(u.id, !u.isAdmin)}
                      className={`${
                        u.isAdmin ? 'text-orange-500' : 'text-purple-500'
                      } text-xs`}
                    >
                      <i
                        className={`fas ${
                          u.isAdmin ? 'fa-user-minus' : 'fa-user-shield'
                        } mr-1`}
                        aria-hidden="true"
                      />
                      {u.isAdmin ? 'Remove Admin' : 'Make Admin'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleBlock(u.id, !u.isBlocked)}
                      className="text-yellow-600 text-xs"
                    >
                      <i
                        className={`fas ${
                          u.isBlocked ? 'fa-unlock-alt' : 'fa-lock'
                        } mr-1`}
                        aria-hidden="true"
                      />
                      {u.isBlocked ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(u)}
                      className="text-red-600 text-xs"
                    >
                      <i className="fas fa-trash-alt mr-1" aria-hidden="true" />{' '}
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {paginated.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="p-8 text-center text-gray-500 text-sm"
                  >
                    <i
                      className="fas fa-user-slash text-3xl mb-2 block"
                      aria-hidden="true"
                    />
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between p-3 border-t dark:border-gray-700 text-xs">
          <span className="text-gray-500">
            Showing {page * PAGE_SIZE + 1}–
            {Math.min((page + 1) * PAGE_SIZE, filtered.length)} of{' '}
            {filtered.length}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
            >
              <i className="fas fa-chevron-left" aria-hidden="true" /> Prev
            </button>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
            >
              Next <i className="fas fa-chevron-right" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <AnimatePresence>
        {profileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[150] p-4"
            onClick={() => setProfileModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold mb-3 text-gray-800 dark:text-white">
                User Profile
              </h3>
              <div className="space-y-1 text-sm text-gray-800 dark:text-gray-200">
                <div>
                  <strong>Name:</strong>{' '}
                  {profileModal.displayName || profileModal.name || '—'}
                </div>
                <div>
                  <strong>Email:</strong> {profileModal.email}
                </div>
                <div>
                  <strong>Role:</strong>{' '}
                  {profileModal.isAdmin
                    ? 'Admin'
                    : profileModal.role || 'Member'}
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  {profileModal.isBlocked ? 'Blocked' : 'Active'}
                </div>
                <div>
                  <strong>Approved:</strong>{' '}
                  {isUserApproved(profileModal)
                    ? 'Yes'
                    : profileModal.registrationStatus === 'rejected'
                    ? 'Rejected'
                    : 'Pending'}
                </div>
                <div>
                  <strong>Member Since:</strong>{' '}
                  {formatDateOnly(profileModal.createdAt)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setProfileModal(null)}
                className="mt-4 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm w-full transition"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {editModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[150] p-4"
            onClick={() => setEditModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                Edit User
              </h3>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                  Display Name
                </label>
                <input
                  type="text"
                  value={editModal.displayName}
                  onChange={(e) =>
                    setEditModal({ ...editModal, displayName: e.target.value })
                  }
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                  Role
                </label>
                <select
                  value={editModal.role}
                  onChange={(e) =>
                    setEditModal({ ...editModal, role: e.target.value })
                  }
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm outline-none focus:ring-2 focus:ring-orange"
                >
                  <option value="Member">Member</option>
                  <option value="Board">Board</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              <div className="flex flex-col gap-2 text-sm dark:text-gray-200">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editModal.isAdmin}
                    onChange={(e) =>
                      setEditModal({ ...editModal, isAdmin: e.target.checked })
                    }
                  />
                  Is Admin
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editModal.isBlocked}
                    onChange={(e) =>
                      setEditModal({
                        ...editModal,
                        isBlocked: e.target.checked,
                      })
                    }
                  />
                  Blocked
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex-1 bg-orange hover:bg-orange-dark text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => setEditModal(null)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg text-sm transition"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite User Modal */}
      <AnimatePresence>
        {inviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[150] p-4"
            onClick={() => setInviteModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 8 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <i className="fas fa-user-plus text-orange" aria-hidden="true" />
                Invite User
              </h3>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                  Email *
                </label>
                <input
                  type="email"
                  value={inviteModal.email}
                  onChange={(e) =>
                    setInviteModal({ ...inviteModal, email: e.target.value })
                  }
                  placeholder="user@example.com"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                  Display Name
                </label>
                <input
                  type="text"
                  value={inviteModal.displayName}
                  onChange={(e) =>
                    setInviteModal({
                      ...inviteModal,
                      displayName: e.target.value,
                    })
                  }
                  placeholder="Juan Dela Cruz"
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
                  Role
                </label>
                <select
                  value={inviteModal.role}
                  onChange={(e) =>
                    setInviteModal({ ...inviteModal, role: e.target.value })
                  }
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm outline-none focus:ring-2 focus:ring-orange"
                >
                  <option value="Member">Member</option>
                  <option value="Board">Board</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={inviteModal.sendInvite}
                  onChange={(e) =>
                    setInviteModal({
                      ...inviteModal,
                      sendInvite: e.target.checked,
                    })
                  }
                />
                Send invitation email
              </label>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={submitInvite}
                  className="flex-1 bg-orange hover:bg-orange-dark text-white py-2 rounded-lg text-sm font-medium transition"
                >
                  Send Invite
                </button>
                <button
                  type="button"
                  onClick={() => setInviteModal(null)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg text-sm transition"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ================================================================== */
/* Comments Tab                                                        */
/* ================================================================== */

const CommentsTab = ({ comments, loading, onDelete, onUpdate }) => {
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const debouncedSearch = useDebounced(search, 150);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return comments;
    return comments.filter(
      (c) =>
        (c.text || '').toLowerCase().includes(q) ||
        (c.authorName || c.author || '').toLowerCase().includes(q),
    );
  }, [comments, debouncedSearch]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditText(c.text || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (c) => {
    await onUpdate?.(c, editText);
    cancelEdit();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
      <div className="p-4 border-b dark:border-gray-700 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <i
            className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search comments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white outline-none focus:ring-2 focus:ring-orange"
          />
        </div>
        <span className="text-xs text-gray-500">
          {filtered.length} of {comments.length}
        </span>
      </div>

      {loading ? (
        <TableSkeleton rows={5} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <i
            className="fas fa-comment-slash text-4xl mb-3"
            aria-hidden="true"
          />
          <p>No comments found</p>
        </div>
      ) : (
        <div className="divide-y dark:divide-gray-700">
          {filtered.map((c) => {
            const isEditing = editingId === c.id;
            return (
              <div
                key={c.id}
                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange/20 text-orange">
                        Comment
                      </span>
                      <span className="text-xs text-gray-500">
                        {c.createdAt?.toLocaleDateString?.() || '—'}
                      </span>
                      {c.editedAt && (
                        <span className="text-xs text-gray-400 italic">
                          · edited
                        </span>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 mt-2 space-y-2">
                        <textarea
                          rows={3}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full p-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white text-sm focus:ring-2 focus:ring-orange focus:outline-none"
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="text-xs px-3 py-1 rounded bg-gray-500 hover:bg-gray-600 text-white"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(c)}
                            className="text-xs px-3 py-1 rounded bg-orange hover:bg-orange-dark text-white"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-100 dark:bg-gray-700/50 rounded-lg p-3 mt-2">
                        <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
                          {c.text}
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      <i className="fas fa-user mr-1" aria-hidden="true" />{' '}
                      {c.authorName || 'Anonymous'}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1 items-end flex-shrink-0">
                    {!isEditing && (
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="text-blue-500 hover:text-blue-700 p-1"
                        aria-label="Edit"
                        title="Edit"
                      >
                        <i className="fas fa-pen" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onDelete(c)}
                      className="text-red-500 hover:text-red-700 p-1"
                      aria-label="Delete"
                      title="Delete"
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/* Contacts Tab                                                        */
/* ================================================================== */

const ContactsTab = ({
  contacts,
  loading,
  onToggleRead,
  onDelete,
  onBulkDelete,
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);

  const debouncedSearch = useDebounced(search, 150);

  const filtered = useMemo(() => {
    let list = contacts;
    if (filter === 'unread') list = list.filter((c) => c.status !== 'read');
    else if (filter === 'read') list = list.filter((c) => c.status === 'read');

    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.subject || '').toLowerCase().includes(q) ||
          (c.message || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [contacts, filter, debouncedSearch]);

  const unreadCount = contacts.filter((c) => c.status !== 'read').length;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filtered.map((c) => c.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkDelete = async () => {
    await onBulkDelete?.([...selected], clearSelection);
  };

  const handleBulkMarkRead = async () => {
    for (const id of selected) {
      const c = contacts.find((x) => x.id === id);
      if (c && c.status !== 'read') await onToggleRead(c);
    }
    clearSelection();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
      <div className="p-4 border-b dark:border-gray-700 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <i
              className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder="Search messages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search messages"
              className="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white outline-none focus:ring-2 focus:ring-orange"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { id: 'all', label: 'All', count: contacts.length },
            { id: 'unread', label: 'Unread', count: unreadCount },
            { id: 'read', label: 'Read', count: contacts.length - unreadCount },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              aria-pressed={filter === f.id}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                filter === f.id
                  ? 'bg-orange text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {f.label}
              {f.count !== null && ` (${f.count})`}
            </button>
          ))}
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 p-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-300 dark:border-teal-700 rounded-lg text-xs">
            <span className="font-medium text-teal-700 dark:text-teal-300">
              {selected.size} selected
            </span>
            <div className="flex-1" />
            <button
              type="button"
              onClick={handleBulkMarkRead}
              className="px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
            >
              <i className="fas fa-check mr-1" aria-hidden="true" /> Mark Read
            </button>
            <button
              type="button"
              onClick={handleBulkDelete}
              className="px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white"
            >
              <i className="fas fa-trash-alt mr-1" aria-hidden="true" /> Delete
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <TableSkeleton rows={6} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <i className="fas fa-inbox text-4xl mb-3" aria-hidden="true" />
          <p>
            {contacts.length === 0
              ? 'No messages yet.'
              : 'No messages match your filters.'}
          </p>
        </div>
      ) : (
        <div className="divide-y dark:divide-gray-700">
          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={
                filtered.length > 0 &&
                filtered.every((c) => selected.has(c.id))
              }
              onChange={toggleSelectAllVisible}
            />
            <span>Select all ({filtered.length})</span>
          </label>

          {filtered.map((c) => {
            const isUnread = c.status !== 'read';
            const isExpanded = expandedId === c.id;
            return (
              <div
                key={c.id}
                className={`p-4 transition ${
                  isUnread ? 'bg-teal-50/50 dark:bg-teal-900/10' : ''
                } hover:bg-gray-50 dark:hover:bg-gray-700/40`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleSelect(c.id)}
                    className="mt-1"
                    aria-label={`Select message from ${c.name}`}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setExpandedId((prev) => (prev === c.id ? null : c.id))
                    }
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {isUnread && (
                        <span className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0" />
                      )}
                      <span className="font-semibold text-sm text-gray-800 dark:text-white truncate">
                        {c.name || 'Anonymous'}
                      </span>
                      {c.subject && (
                        <span className="text-xs text-gray-500 truncate">
                          — {c.subject}
                        </span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
                        {timeAgo(c.createdAt)}
                      </span>
                    </div>

                    <p
                      className={`text-xs text-gray-500 truncate ${
                        isUnread ? 'font-medium' : ''
                      }`}
                    >
                      {c.email}
                    </p>

                    <p
                      className={`text-sm mt-2 text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words ${
                        isExpanded ? '' : 'line-clamp-2'
                      }`}
                    >
                      {c.message}
                    </p>
                  </button>

                  <div className="flex flex-col gap-1 items-end flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => onToggleRead?.(c)}
                      className="text-xs text-blue-500 hover:underline whitespace-nowrap"
                      title={isUnread ? 'Mark as read' : 'Mark as unread'}
                    >
                      {isUnread ? (
                        <>
                          <i className="fas fa-check" aria-hidden="true" /> Read
                        </>
                      ) : (
                        <>
                          <i className="fas fa-undo" aria-hidden="true" /> Unread
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete?.(c)}
                      className="text-xs text-red-500 hover:underline whitespace-nowrap"
                      title="Delete"
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />{' '}
                      Delete
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="mt-3 ml-7 flex flex-wrap gap-2">
                    <a
                      href={`mailto:${c.email}?subject=${encodeURIComponent(
                        `Re: ${c.subject || 'Your message'}`,
                      )}`}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-orange hover:bg-orange-dark text-white transition"
                    >
                      <i className="fas fa-reply" aria-hidden="true" /> Reply via email
                    </a>
                    <a
                      href={`mailto:${c.email}`}
                      className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                      <i className="fas fa-at" aria-hidden="true" /> {c.email}
                    </a>
                    {c.userId && (
                      <Link
                        to={`/profile/${c.userId}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                      >
                        <i className="fas fa-user" aria-hidden="true" /> View profile
                      </Link>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/* Notifications Tab                                                   */
/* ================================================================== */

const NotificationsTab = ({
  notificationsList,
  loading,
  onDelete,
  onBulkDelete,
  onEdit,
  onSend,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  const debouncedSearch = useDebounced(search, 150);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return notificationsList;
    return notificationsList.filter(
      (n) =>
        (n.title || '').toLowerCase().includes(q) ||
        (n.body || '').toLowerCase().includes(q),
    );
  }, [notificationsList, debouncedSearch]);

  useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(notificationsList.map((n) => n.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [notificationsList]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((n) => selected.has(n.id));

  const toggleSelectAll = () => {
    const visibleIds = filtered.map((n) => n.id);
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const typeColor = (type) => {
    switch (type) {
      case 'post_approved':
      case 'story_approved':
      case 'user_approved':
        return 'bg-green-500';
      case 'post_rejected':
      case 'story_rejected':
      case 'user_rejected':
        return 'bg-red-500';
      case 'award_added':
      case 'award_updated':
        return 'bg-yellow-500';
      case 'announcement':
        return 'bg-orange';
      default:
        return 'bg-gray-400';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          Recent Notifications
        </h2>
        <div className="flex gap-2">
          <div className="relative flex-1 sm:flex-initial sm:min-w-[200px]">
            <i
              className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
              aria-hidden="true"
            />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white outline-none focus:ring-2 focus:ring-orange"
            />
          </div>
          <button
            type="button"
            onClick={onSend}
            className="bg-orange hover:bg-orange-dark text-white px-3 py-1.5 rounded-lg text-sm font-medium flex-shrink-0 transition"
          >
            <i className="fas fa-paper-plane mr-1" aria-hidden="true" /> Send
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-2 mb-3 bg-orange/5 border border-orange/30 rounded-lg text-xs">
          <span className="font-medium text-orange">
            {selected.size} selected
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => onBulkDelete?.([...selected], clearSelection)}
            className="px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white"
          >
            <i className="fas fa-trash-alt mr-1" aria-hidden="true" /> Delete
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          >
            Clear
          </button>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={4} />
      ) : filtered.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center">
          <i
            className="fas fa-bell-slash text-3xl mb-2 block"
            aria-hidden="true"
          />
          {notificationsList.length === 0
            ? 'No notifications sent yet.'
            : 'No notifications match your search.'}
        </p>
      ) : (
        <div className="space-y-1">
          <label className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-50 dark:bg-gray-700/50 text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
            />
            <span>
              Select all
              {search.trim()
                ? ` (filtered: ${filtered.length})`
                : ` (${filtered.length})`}
            </span>
          </label>

          {filtered.map((n) => (
            <div
              key={n.id}
              className={`border-b dark:border-gray-700 pb-2 flex justify-between items-start gap-3 text-sm transition ${
                selected.has(n.id)
                  ? 'bg-orange/5 dark:bg-orange/10 rounded px-2'
                  : 'px-2'
              }`}
            >
              <div className="flex gap-2 min-w-0 flex-1">
                <input
                  type="checkbox"
                  checked={selected.has(n.id)}
                  onChange={() => toggleSelect(n.id)}
                  className="mt-1 flex-shrink-0"
                  aria-label={`Select notification: ${n.title}`}
                />
                <span
                  className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${typeColor(
                    n.type,
                  )}`}
                  aria-hidden="true"
                />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 dark:text-white">
                    {n.title}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    To:{' '}
                    {n.recipientType === 'all'
                      ? 'All users'
                      : n.recipientType === 'roles'
                      ? (n.recipients || []).join(', ')
                      : `${(n.recipients || []).length} user(s)`}
                    {n.createdByName && ` · by ${n.createdByName}`}
                    {n.updatedAt && ' · edited'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0 items-end text-xs">
                <button
                  type="button"
                  onClick={() => onEdit(n)}
                  className="text-blue-500 hover:underline"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(n.id)}
                  className="text-red-500 hover:underline"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ================================================================== */
/* Android App Tab (ADDED)                                             */
/* ================================================================== */

const AndroidAppTab = ({ user, showToast, confirm }) => {
  const [release, setRelease] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [versionName, setVersionName] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const fileInputRef = useRef(null);

  /* Live view of current release */
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'appReleases', 'android'),
      (snap) => {
        setRelease(snap.exists() ? snap.data() : null);
        setReleaseLoading(false);
      },
      (err) => {
        console.warn('appReleases listener:', err);
        setReleaseLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setStatus(null);
    setProgress(0);
    if (f) {
      const m = f.name.match(/(\d+\.\d+\.\d+)/);
      if (m && !versionName) setVersionName(m[1]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    if (!/\.apk$/i.test(file.name)) {
      setStatus({ type: 'error', msg: 'Only .apk files are allowed.' });
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      setStatus({ type: 'error', msg: 'APK must be under 150 MB.' });
      return;
    }

    setUploading(true);
    setProgress(0);
    setStatus(null);

    try {
      const path = `android-app/releases/${Date.now()}-${file.name.replace(
        /[^\w.\-]/g,
        '_',
      )}`;
      const sref = storageRef(storage, path);
      const task = uploadBytesResumable(sref, file, {
        contentType: 'application/vnd.android.package-archive',
      });

      task.on(
        'state_changed',
        (snap) =>
          setProgress(
            Math.round((snap.bytesTransferred / snap.totalBytes) * 100),
          ),
        (err) => {
          setUploading(false);
          setStatus({ type: 'error', msg: `Upload failed: ${err.message}` });
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);

            // Delete previous APK from Storage (keeps quota tidy)
            if (release?.storagePath && release.storagePath !== path) {
              try {
                await deleteObject(storageRef(storage, release.storagePath));
              } catch (cleanupErr) {
                console.warn('Could not delete old APK:', cleanupErr);
              }
            }

            await setDoc(
              doc(db, 'appReleases', 'android'),
              {
                url,
                storagePath: path,
                version: versionName.trim() || file.name,
                fileName: file.name,
                sizeBytes: file.size,
                uploadedBy: user?.uid || null,
                uploadedByName: user?.displayName || user?.email || '',
                uploadedAt: serverTimestamp(),
              },
              { merge: true },
            );

            setUploading(false);
            setProgress(100);
            setStatus({
              type: 'success',
              msg: 'Upload complete — footer download link updated.',
            });
            setFile(null);
            setVersionName('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            showToast('APK uploaded', 'success');
          } catch (err) {
            console.error('publish failed:', err);
            setUploading(false);
            setStatus({
              type: 'error',
              msg: `Failed to publish: ${err.message}`,
            });
          }
        },
      );
    } catch (err) {
      setUploading(false);
      setStatus({ type: 'error', msg: err.message });
    }
  };

  const handleDeleteRelease = async () => {
    const ok = await confirm({
      title: 'Delete current APK?',
      message:
        'The download link in the footer will be removed until you upload a new APK.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;

    try {
      if (release?.storagePath) {
        try {
          await deleteObject(storageRef(storage, release.storagePath));
        } catch (err) {
          console.warn('Storage delete failed:', err);
        }
      }
      await setDoc(
        doc(db, 'appReleases', 'android'),
        {
          url: null,
          storagePath: null,
          version: null,
          fileName: null,
          sizeBytes: 0,
          uploadedBy: null,
          uploadedByName: '',
          uploadedAt: null,
        },
        { merge: true },
      );
      showToast('Release removed');
    } catch (err) {
      console.error(err);
      showToast('Failed to remove release', 'error');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Download URL copied');
    } catch {
      showToast('Could not copy', 'error');
    }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Current release card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Current release
          </h2>
          {release?.url && (
            <button
              type="button"
              onClick={handleDeleteRelease}
              className="text-xs text-red-500 hover:underline"
            >
              <i className="fas fa-trash-alt mr-1" aria-hidden="true" />
              Remove
            </button>
          )}
        </div>

        {releaseLoading ? (
          <div className="space-y-2 animate-pulse">
            <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-2 w-48 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ) : release?.url ? (
          <div className="space-y-2 text-sm">
            <p className="text-gray-900 dark:text-white flex items-center gap-2 font-medium">
              <i
                className="fab fa-android text-green-500 text-lg"
                aria-hidden="true"
              />
              {release.version || 'Unnamed build'}
            </p>
            <p className="text-xs text-gray-500">
              {release.sizeBytes ? `${formatBytes(release.sizeBytes)} · ` : ''}
              {release.uploadedAt?.toDate?.().toLocaleString?.() || ''}
              {release.uploadedByName
                ? ` · by ${release.uploadedByName}`
                : ''}
            </p>
            <div className="flex flex-wrap gap-2 items-center">
              <a
                href={release.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-orange hover:underline break-all"
              >
                <i className="fas fa-download" aria-hidden="true" />
                {release.url}
              </a>
              <button
                type="button"
                onClick={() => copyToClipboard(release.url)}
                className="text-xs text-gray-500 hover:text-orange"
                title="Copy URL"
              >
                <i className="fas fa-copy" aria-hidden="true" /> Copy
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No APK uploaded yet. Upload one below — the download button in the
            site footer will appear automatically.
          </p>
        )}
      </div>

      {/* Upload form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Upload new build
        </h2>

        <label className="block mb-3">
          <span className="text-xs text-gray-600 dark:text-gray-400">
            APK file (.apk only, max 150 MB)
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={handleFileChange}
            disabled={uploading}
            className="block w-full mt-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-orange file:text-white hover:file:bg-orange-dark file:cursor-pointer disabled:opacity-50 dark:text-gray-200"
          />
          {file && (
            <p className="text-xs text-gray-500 mt-1">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}
        </label>

        <label className="block mb-3">
          <span className="text-xs text-gray-600 dark:text-gray-400">
            Version name
          </span>
          <input
            type="text"
            value={versionName}
            onChange={(e) => setVersionName(e.target.value)}
            placeholder="e.g. 2.3.1"
            disabled={uploading}
            className="block w-full mt-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange"
          />
        </label>

        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {uploading ? `Uploading… ${progress}%` : 'Upload & Publish'}
        </button>

        {uploading && (
          <div className="mt-3 w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {status && (
          <div
            className={`mt-3 text-sm px-3 py-2 rounded-lg ${
              status.type === 'error'
                ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                : 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            }`}
          >
            {status.msg}
          </div>
        )}
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-xs text-yellow-800 dark:text-yellow-200 flex gap-2">
        <i className="fas fa-info-circle mt-0.5" aria-hidden="true" />
        <div>
          Users downloading an APK outside the Play Store need to enable
          <strong> "Install unknown apps"</strong> for their browser. They will
          see a warning before installing — this is normal for sideloaded APKs.
        </div>
      </div>
    </div>
  );
};

export default Admin;